import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Building, Network, ArrowRight } from 'lucide-react';

const ECommerceModelsSection = () => {
  const models = [
    {
      icon: <Users className="h-16 w-16 text-blue-600" />,
      title: "B2C Online-Shops",
      subtitle: "Business-to-Consumer",
      description: "Klassische Online-Shops für Endkunden mit Fokus auf Benutzerfreundlichkeit und Conversion-Optimierung.",
      features: [
        "Intuitive Benutzerführung und mobiles Design",
        "Schnelle Checkout-Prozesse",
        "Produktbewertungen und Empfehlungen",
        "Social Media Integration",
        "Newsletter und Kundenbindung",
        "SEO und Content-Marketing"
      ],
      examples: "Fashion, Elektronik, Lifestyle, Lebensmittel",
      highlight: "Fokus auf Emotion und Kaufimpuls"
    },
    {
      icon: <Building className="h-16 w-16 text-green-600" />,
      title: "B2B E-Commerce Portale",
      subtitle: "Business-to-Business",
      description: "Professionelle Geschäftskundenportale mit komplexen Preisstrukturen und Bestellprozessen.",
      features: [
        "Kundenspezifische Preise und Konditionen",
        "Bestelllisten und Wiederbestellungen",
        "Freigabeprozesse und Budgetkontrollen",
        "Individuelle Produktkataloge",
        "ERP-Integration und Datenabgleich",
        "Detaillierte Reporting-Funktionen"
      ],
      examples: "Großhandel, Industriebedarf, Medizintechnik",
      highlight: "Effizienz und Prozessoptimierung"
    },
    {
      icon: <Network className="h-16 w-16 text-purple-600" />,
      title: "Großkunden-Portale",
      subtitle: "Mehrstufige Beschaffung",
      description: "Spezialisierte Portale für Franchise-Systeme und Großkunden mit mehrstufigen Beschaffungsprozessen.",
      features: [
        "Standort-spezifische Produktkataloge",
        "Zentrale vs. dezentrale Beschaffung",
        "Budgetverteilung auf Standorte",
        "Mehrstufige Freigabeprozesse",
        "Franchise-Geber Reporting",
        "Lieferanten-Integration"
      ],
      examples: "Franchise-Ketten, Filialsysteme, Konzerne",
      highlight: "Zentrale Kontrolle, dezentrale Flexibilität"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Verschiedene <span className="text-blue-600">E-Commerce Modelle</span>
          </h2>
          <p className="text-lg text-gray-600">
            Jedes Geschäftsmodell hat andere Anforderungen. Wir entwickeln die passende Lösung für deine Zielgruppe.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {models.map((model, index) => (
            <Card key={index} className="border-0 shadow-xl bg-white h-full">
              <CardContent className="p-8 h-full flex flex-col">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    {model.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{model.title}</h3>
                  <p className="text-lg font-semibold text-gray-600">{model.subtitle}</p>
                </div>
                
                <p className="text-gray-700 mb-6 leading-relaxed flex-grow">
                  {model.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-3">Wichtige Funktionen:</h4>
                  <ul className="space-y-2">
                    {model.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start text-sm">
                        <span className="text-blue-500 mr-2 mt-1">•</span>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-2">Typische Branchen:</h4>
                  <p className="text-sm text-gray-600">{model.examples}</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mt-auto">
                  <p className="text-blue-800 font-semibold text-sm">
                    💡 {model.highlight}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Multi-Channel Strategy */}
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Multi-Channel E-Commerce Strategie</h3>
            <p className="text-lg text-gray-600">
              Verkaufe nicht nur in deinem eigenen Shop - nutze alle verfügbaren Kanäle für maximale Reichweite.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl mb-2">🛒</div>
              <h4 className="font-bold text-gray-900 mb-2">Eigener Shop</h4>
              <p className="text-sm text-gray-600">Shopware oder Custom Platform</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl mb-2">📦</div>
              <h4 className="font-bold text-gray-900 mb-2">Amazon</h4>
              <p className="text-sm text-gray-600">Marketplace Integration</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl mb-2">🏪</div>
              <h4 className="font-bold text-gray-900 mb-2">eBay & Otto</h4>
              <p className="text-sm text-gray-600">Weitere Marktplätze</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl mb-2">📱</div>
              <h4 className="font-bold text-gray-900 mb-2">Social Commerce</h4>
              <p className="text-sm text-gray-600">Facebook, Instagram, TikTok</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-600 mb-6">
              Wir helfen dir dabei, alle Kanäle zentral zu verwalten und deine Produkte überall gleichzeitig zu verkaufen.
            </p>
            <a 
              href="#contact" 
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Multi-Channel Strategie besprechen
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ECommerceModelsSection; 